import type { Coding, Resource } from '@medplum/fhirtypes'
import { isOfType } from '#src/utils.ts'

export const getTag = (
  resource: Resource,
  system: string
): Coding | undefined => resource?.meta?.tag?.find((t) => t.system === system)

export const getTags = (resource: Resource, system?: string): Coding[] =>
  resource?.meta?.tag?.filter((t) => !system || t.system === system) ?? []

export const hasTag = (resource: Resource, tag: Coding): boolean =>
  !!resource?.meta?.tag?.some((t) => matchTag(t, tag))

/**
 * Adds a tag to a resource, allows multiple tags with the same system
 * @param resource - The resource to add the tag to.
 * @param tag - The tag to add.
 */
export const addTag = (resource: Resource, tag: Coding): void => {
  if (isOfType(resource, 'Bundle')) {
    resource.entry?.forEach((e) => {
      if (e.resource) {
        addTag(e.resource, tag)
      }
    })
    return
  }

  resource.meta ??= {}
  resource.meta.tag ??= []

  const newTags = resource.meta.tag.filter((t) => !matchTag(t, tag))
  newTags.push(tag)
  resource.meta.tag = newTags
}

/**
 * Sets a tag in the resource's metadata.
 * @param resource - The resource to set the tag in.
 * @param tag - The tag to set.
 */
export const setTag = (resource: Resource, tag: Coding): void => {
  resource.meta ??= {}
  resource.meta.tag ??= []

  const newTags = resource.meta.tag.filter((t) => t.system !== tag.system)
  newTags.push(tag)
  resource.meta.tag = newTags
}

export const removeTag = (resource: Resource, tag: Coding): void => {
  if (isOfType(resource, 'Bundle')) {
    resource.entry?.forEach((e) => {
      if (e.resource) {
        removeTag(e.resource, tag)
      }
    })
    return
  }

  if (!resource.meta?.tag) {
    return
  }

  const newTags = resource.meta.tag.filter((t) => !matchTag(t, tag))
  // medplum automatically removes empty arrays on tags so we need to match the behavior
  resource.meta.tag = newTags.length > 0 ? newTags : undefined
}

export const toggleTag = (resource: Resource, tag: Coding): void =>
  hasTag(resource, tag) ? removeTag(resource, tag) : addTag(resource, tag)

export function matchTag(tag: Coding, query: Coding): boolean {
  return tag.system === query.system && (!query.code || tag.code === query.code)
}
